import { Link, useSearchParams } from "react-router-dom";
import { useState } from "react";
import PageBanner from "@/components/feature/PageBanner";
import ProductCard from "@/components/feature/ProductCard";
import Reveal from "@/components/feature/Reveal";
import { products, productCategories } from "@/mocks/products";

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "전체";
  const keyword = searchParams.get("q") || "";
  const [searchInput, setSearchInput] = useState(keyword);

  const filtered = products.filter((product) => {
    const matchesCategory = activeCategory === "전체" || product.category === activeCategory;
    const lowerKeyword = keyword.trim().toLowerCase();
    const matchesKeyword =
      !lowerKeyword ||
      product.name.toLowerCase().includes(lowerKeyword) ||
      product.nameEn.toLowerCase().includes(lowerKeyword) ||
      product.category.toLowerCase().includes(lowerKeyword) ||
      product.material.toLowerCase().includes(lowerKeyword);
    return matchesCategory && matchesKeyword;
  });

  const selectCategory = (category: string) => {
    const next = new URLSearchParams(searchParams);
    if (category === "전체") {
      next.delete("category");
    } else {
      next.set("category", category);
    }
    setSearchParams(next);
  };

  const submitSearch = () => {
    const next = new URLSearchParams(searchParams);
    if (searchInput.trim()) {
      next.set("q", searchInput.trim());
    } else {
      next.delete("q");
    }
    setSearchParams(next);
  };

  return (
    <>
      <PageBanner
        eyebrow="PRODUCT"
        title="제품소개"
        description="주방 상판부터 욕실, 바닥, 외벽까지. 공간과 용도에 맞는 석재를 카테고리별로 살펴보세요."
        image="https://readdy.ai/api/search-image?query=various%20polished%20stone%20slabs%20in%20different%20colors%20displayed%20in%20a%20bright%20modern%20showroom%2C%20white%20grey%20beige%20and%20black%20marble%20samples%2C%20soft%20natural%20lighting%2C%20high%20end%20material%20photography&width=1800&height=900&seq=stone-banner-products&orientation=landscape"
        imageAlt="다양한 색상의 석재 판재 전시"
        current="제품소개"
      />

      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          {/* Search */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-foreground-600">
              총 <span className="font-medium text-primary-600">{filtered.length}</span>개의 소재
              {keyword && <span className="text-foreground-500"> · '{keyword}' 검색 결과</span>}
            </p>
            <div className="flex w-full items-center gap-2 md:w-auto">
              <div className="relative flex-1 md:w-72">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") submitSearch();
                  }}
                  placeholder="제품명 또는 재질 검색"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <button
                type="button"
                onClick={submitSearch}
                className="h-11 cursor-pointer whitespace-nowrap rounded-md bg-foreground-950 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
              >
                검색
              </button>
            </div>
          </div>

          {/* Category filter */}
          <div className="mt-6 flex flex-wrap gap-2">
            {productCategories.map((category) => {
              const isActive = activeCategory === category;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => selectCategory(category)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${
                    isActive
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>

          {/* Grid */}
          {filtered.length > 0 ? (
            <div data-product-shop className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((product, index) => (
                <Reveal key={product.id} delay={Math.min(index, 6) * 60}>
                  <ProductCard product={product} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-16 flex flex-col items-center rounded-lg border border-background-200 bg-background-100 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h3 className="mt-5 font-heading text-xl text-foreground-950">검색 결과가 없습니다</h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                다른 키워드로 검색하시거나 카테고리를 변경해 보세요. 원하시는 소재를 찾지 못하셨다면 상담을 통해
                안내받으실 수 있습니다.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setSearchParams(new URLSearchParams());
                  }}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-foreground-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  필터 초기화
                </button>
                <Link
                  to="/contact"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  소재 상담 문의
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}